import {
  type BubbleUserDto,
  useBackgroundCheckCreateDistrictPay,
  useBackgroundCheckGetProducts,
  useBubbleBulkAddUsersAsRole,
  useBubbleGetRoles,
  useBubbleGetUpcomingRsvps,
  useBubbleGetUsersForBubble,
  type UserDistrictSummary,
  useSchoolDistrictApproveVettedUser,
} from "@crowdcoursing/api/openapi";
import isEmpty from "lodash/isEmpty";
import { useCallback, useMemo } from "react";

import { useToast } from "~hooks/useToast";

import { type WorksheetGeneral } from "../../../cc-ui/hooks";
import { useDispatch, useSelector } from "../../../hooks";
import { errorToast, successToast } from "../../../state/notifications";
import { getParsedSelected } from "../../atoms/Form";
import { type SchoolAdminTemplateProps } from "../../templates/AdminTemplate/AdminTemplate";
import { type School } from "./utils";

const tApproveSuccessMessage = (name: string) => `${name} approved!`;
const tApproveErrorMessage = "Failed to approve volunteer";

/** Matches School Users / add-user allowlist — bubble membership role for students */
export const BUBBLE_STUDENT_ROLE = "Student";

export function isUserStudentInBubble(user: UserDistrictSummary, bubbleId: string): boolean {
  if (!bubbleId) {
    return false;
  }
  if ((user.roles ?? []).includes(BUBBLE_STUDENT_ROLE)) {
    return true;
  }
  return user.membershipStatuses?.some((m) => m.bubbleId === bubbleId && m.role === BUBBLE_STUDENT_ROLE) ?? false;
}

export function userDistrictSummaryDisplayName(user: UserDistrictSummary): string {
  const first = user.firstName?.trim() ?? "";
  const last = user.lastName?.trim() ?? "";
  const full = `${first} ${last}`.trim();
  return full || "Unknown";
}

/** Same data as School Users (`useBubbleGetUsersForBubble`), filtered to Student role only */
export function useBubbleStudentUsers(bubbleId: string) {
  const { data, refetch, status } = useBubbleGetUsersForBubble({ pathParams: { bubbleId } }, { enabled: !!bubbleId });
  const students = useMemo(() => (data ?? []).filter((u) => isUserStudentInBubble(u, bubbleId)), [data, bubbleId]);
  return { refetch, status, students } as const;
}

/** School bubble users who are not in the Student role (faculty / staff / etc.) */
export function useBubbleNonStudentUsers(bubbleId: string) {
  const { data, refetch, status } = useBubbleGetUsersForBubble({ pathParams: { bubbleId } }, { enabled: !!bubbleId });
  const users = useMemo(() => (data ?? []).filter((u) => !isUserStudentInBubble(u, bubbleId)), [data, bubbleId]);
  return { refetch, status, users } as const;
}

export const useSchoolUsersProps = (
  bubbleId = "",
): Omit<
  SchoolAdminTemplateProps["userManagementProps"]["schoolUsersProps"],
  "approvalListProps" | "addUserButtonProps"
> => {
  const toast = useToast();
  const { data, refetch, status } = useBubbleGetUsersForBubble({ pathParams: { bubbleId } }, { enabled: !!bubbleId });
  const { mutateAsync } = useBackgroundCheckCreateDistrictPay();
  const { data: bgProducts } = useBackgroundCheckGetProducts(
    { queryParams: { BubbleId: bubbleId } },
    { enabled: !!bubbleId },
  );

  const { mutateAsync: approveUser } = useSchoolDistrictApproveVettedUser({
    onError(error) {
      window.console.log({ error });
      toast.error(tApproveErrorMessage);
    },
  });

  const getOnInitiateBackgroundCheck = useCallback(
    (
      pathParams: Parameters<typeof mutateAsync>[0]["pathParams"],
      body: Omit<Parameters<typeof mutateAsync>[0]["body"], "productId">,
    ) =>
      (productId: string) => {
        mutateAsync({ body: { ...body, productId }, pathParams });
      },
    [mutateAsync],
  );

  const getOnApprove = useCallback(
    ({ id: userId, name }: { id: string; name: string }) =>
      async () => {
        await approveUser(
          { pathParams: { bubbleId, userId } },
          {
            onSuccess() {
              toast.success(tApproveSuccessMessage(name));
            },
          },
        );
      },
    [approveUser, bubbleId, toast],
  );

  const getOnReject = useCallback(
    (_user: { id: string; name: string }) => async () => {
      // todo: handle deny - we'll need a new endpoint
    },
    [],
  );

  return {
    data: (data ?? []).map(
      ({
        backgroundCheckUrl,
        email,
        firstName,
        isBackgroundCheckCompleted,
        lastName,
        membershipStatuses,
        userId,
        schoolDistrictId,
        roles,
        status,
      }) => {
        return {
          actions: {
            onInitiateBackgroundCheck: getOnInitiateBackgroundCheck(
              { bubbleId },
              {
                email: email ?? "",
                firstName: firstName ?? "",
                lastName: lastName ?? "",
                schoolDistrictId: schoolDistrictId ?? "",
                userId: userId ?? "",
              },
            ),
          },
          backgroundCheckProducts: bgProducts || [],
          backgroundCheckUrl: backgroundCheckUrl ?? "",
          bubbleId: bubbleId,
          email,
          firstName,
          isBackgroundCheckCompleted: isBackgroundCheckCompleted ?? false,
          lastName,
          membership: (membershipStatuses ?? []).map(({ role, status, bubbleId: id, bubbleName: name }) => {
            return {
              actions: {
                onApprove: getOnApprove({ id: userId ?? "", name: `${firstName} ${lastName}` }),
                onReject: getOnReject({ id: userId ?? "", name: `${firstName} ${lastName}` }),
              },
              id: id ?? "",
              isVetted: status === "Vetted",
              name: name ?? "",
              roles: role ? [role] : [],
            };
          }),
          phoneNumber: "",
          roles,
          status,
          userId: userId ?? "",
        };
      },
    ),
    refetch,
    status,
  };
};

export const useSchoolRsvpsProps = (
  bubbleId = "",
): SchoolAdminTemplateProps["userManagementProps"]["schoolRsvpsProps"] => {
  const { data, refetch, status } = useBubbleGetUpcomingRsvps({ pathParams: { bubbleId } }, { enabled: !!bubbleId });
  return {
    data: (data ?? []).map(
      ({
        eventId,
        eventName,
        firstName,
        lastName,
        meetingTimeDto,
        email,
        phoneNumber,
        isVetted,
        ownerFirstName,
        ownerLastName,
        roomId,
        roomName,
        userId,
      }) => {
        const contactInformation = { email: email ?? undefined, phone: phoneNumber ?? undefined };
        const event = { id: eventId ?? undefined, name: eventName ?? undefined };
        const meetingTime = {
          beginMeetingTime: new Date(meetingTimeDto?.beginMeetingTime ?? Number.MAX_SAFE_INTEGER),
          endMeetingTime: new Date(meetingTimeDto?.endMeetingTime ?? Number.MAX_SAFE_INTEGER),
        };
        const room = { id: roomId ?? undefined, name: roomName ?? undefined };

        return {
          attendeeName: `${lastName}, ${firstName}`,
          attendeeUserId: userId ?? "",
          contactInformation: isEmpty(contactInformation) ? undefined : contactInformation,
          event: isEmpty(event) ? undefined : event,
          isVetted: isVetted ?? false,
          meetingTime: isEmpty(meetingTime) ? undefined : meetingTime,
          ownerFirstName: ownerFirstName ?? "",
          ownerLastName: ownerLastName ?? "",
          room: isEmpty(room) ? undefined : room,
          rsvpId: meetingTimeDto?.id ?? "",
        };
      },
    ),
    refetch,
    status,
  };
};

export const useAddUserButtonProps = (
  school: School,
): SchoolAdminTemplateProps["userManagementProps"]["schoolUsersProps"]["addUserButtonProps"] => {
  const userId = useSelector((state) => state.user?.id);
  const { mutate } = useBubbleBulkAddUsersAsRole();

  const { data: rolesData } = useBubbleGetRoles(
    { pathParams: { bubbleId: school.bubbleId } },
    { enabled: !!school.bubbleId },
  );

  return {
    onSubmit: ({ email, firstName, lastName, roleSelect, schoolSelect }) => {
      if (roleSelect && schoolSelect) {
        mutate({
          body: {
            bulkAddUserDtos: [
              {
                bubbleName: getParsedSelected(schoolSelect).value,
                email,
                firstName,
                lastName,
                role: getParsedSelected(roleSelect).value,
                userId,
              },
            ],
          },
          pathParams: { bubbleId: school.bubbleId },
        });
      }
    },
    roles: rolesData?.map((role) => ({ id: role, value: role })) ?? [],
    schools: [{ id: school.id, value: school.name }],
  };
};

export const useBulkUploadProps = (
  _schoolId = "",
  bubbleId = "",
  schoolName = "",
): SchoolAdminTemplateProps["userManagementProps"]["schoolUsersProps"]["bulkUploadProps"] => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user?.id);

  const { data: rolesData } = useBubbleGetRoles({ pathParams: { bubbleId } }, { enabled: !!bubbleId });

  const { mutate } = useBubbleBulkAddUsersAsRole();

  const onSubmit = (worksheet?: WorksheetGeneral) => () => {
    if (!userId) {
      dispatch(errorToast({ message: "User ID is required for bulk upload" }));
      return;
    }

    if (!bubbleId) {
      dispatch(errorToast({ message: "School bubble ID is required for bulk upload" }));
      return;
    }

    if (!worksheet) {
      dispatch(errorToast({ message: "Worksheet is required for bulk upload" }));
      return;
    }

    const bulkAddUserDtos = worksheet.rows.map((row) => {
      const [firstName, lastName, email, school, role] = row;
      return { email, firstName, lastName, role, school, userId } as BubbleUserDto;
    });

    if (!bulkAddUserDtos.length) {
      dispatch(errorToast({ message: "No rows found in the uploaded file" }));
      return;
    }

    mutate(
      {
        body: { bulkAddUserDtos },
        pathParams: { bubbleId },
      },
      {
        onError: () => {
          dispatch(errorToast({ message: "Failed to upload users" }));
        },
        onSuccess: () => {
          dispatch(successToast({ message: "Users successfully uploaded" }));
        },
      },
    );
  };

  return { allowedRoles: rolesData ?? [], onSubmit, schools: [schoolName].filter(Boolean) };
};
